/**
 * RichText.tsx
 *
 * Safely renders HTML strings that may contain <b>, <i>, <u>, <strong>, <em>, <a>, <p>, <div>, <br> tags
 * produced by the admin rich text editor.
 *
 * Only allows a safe whitelist of formatting tags — no scripts, no iframes.
 */

import { useMemo } from "react";

interface RichTextProps {
  /** HTML string from the admin editor (may contain <b>, <i>, <u>, <br>, <a>, <p>, <div>, etc.) */
  html: string;
  /** Additional className applied to the wrapper */
  className?: string;
  /** Wrapper element tag. Defaults to "span" for inline use. Use "div" for block. */
  as?: "span" | "div" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /** Optional inline styles */
  style?: React.CSSProperties;
}

/**
 * Decode HTML entities for plain text that got accidentally encoded.
 * e.g. "Hello &amp; World" -> "Hello & World"
 */
function decodeEntities(text: string): string {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&nbsp;/g, "\u00a0");
}

/**
 * Sanitizes an HTML string to only allow safe formatting tags.
 * Strips anything that isn't <b>, <strong>, <i>, <em>, <u>, <br>, <span>, <a>, <p>, <div>.
 */
function sanitizeHtml(html: string): { html: string; hasBlockTags: boolean; hasNewlines: boolean } {
  if (!html) return { html: "", hasBlockTags: false, hasNewlines: false };

  // If it's plain text (no tags), decode entities, convert \n to <br>, and return
  if (!html.includes("<")) {
    const decoded = decodeEntities(html);
    const hasNewlines = /\r?\n/.test(decoded);
    // Convert \n to <br> for visual line breaks
    const withBreaks = decoded.replace(/\r?\n/g, "<br>");
    return { html: withBreaks, hasBlockTags: false, hasNewlines };
  }

  // Allow safe block and inline tags
  const allowed = /^(b|strong|i|em|u|br|span|a|p|div)$/i;

  let hasBlockTags = false;

  const clean = html.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g, (match, tag) => {
    if (allowed.test(tag)) {
      if (tag.toLowerCase() === "p" || tag.toLowerCase() === "div") {
        hasBlockTags = true;
      }
      // For closing tags, keep them clean
      if (match.startsWith("</")) return `</${tag.toLowerCase()}>`;
      // For self-closing br
      if (tag.toLowerCase() === "br") return "<br>";
      // For anchor tags, preserve href and target attributes (safe ones only)
      if (tag.toLowerCase() === "a") {
        const hrefMatch = match.match(/href="([^"]*?)"/);
        const targetMatch = match.match(/target="([^"]*?)"/);
        const href = hrefMatch ? hrefMatch[1] : "#";
        const safeHref = /^(https?:\/\/|mailto:|#|\/)/i.test(href) ? href : "#";
        const target = targetMatch ? ` target="${targetMatch[1]}" rel="noopener noreferrer"` : "";
        return `<a href="${safeHref}"${target} class="text-pink-500 underline hover:text-pink-700">`;
      }
      // For opening tags, strip all attributes except style (for span)
      if (tag.toLowerCase() === "span") {
        const styleMatch = match.match(/style="([^"]*?)"/);
        const style = styleMatch ? ` style="${styleMatch[1]}"` : "";
        return `<${tag.toLowerCase()}${style}>`;
      }
      return `<${tag.toLowerCase()}>`;
    }
    // Strip disallowed tags entirely
    return "";
  });

  return { html: clean, hasBlockTags, hasNewlines: false };
}

export default function RichText({ html, className = "", as: Tag = "span", style }: RichTextProps) {
  const { html: clean, hasBlockTags, hasNewlines } = useMemo(() => sanitizeHtml(html), [html]);

  // If content has block tags (p, div), render as div regardless of requested tag
  // to avoid invalid nesting like <p><p>...</p></p>
  const Wrapper = hasBlockTags ? "div" : Tag;

  const wrapperStyle: React.CSSProperties = {
    ...style,
    ...(hasNewlines ? { whiteSpace: "pre-line" } : {}),
  };

  return (
    <Wrapper
      className={className}
      style={wrapperStyle}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}